import axios from 'axios';
import { useState, useEffect, useRef, useCallback } from 'react';
import React from 'react';
import { getAuthToken } from '../../utils/cookieUtils';
import { 
  saveFacilityToStorage, 
  loadFacilityFromStorage 
} from '../../utils/facilityStorage';

// Add GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Types for yard spots and activities
export type ScheduledTime = {
  startTime: Date;
  endTime: Date;
  driverId: string | null;
  driverName: string | null;
  trailerId: string | null;
  trailerNumber: string | null;
  shipmentNumber: string | null;
};

export type Status = 'available' | 'in-use' | 'inactive' | 'loaded' | 'empty' | 'unavailable';

export type Zone = {
  id: string;
  name: string;
  color: string;
  zone_type?: string;
};

export type YardSpot = {
  id: string;
  number: number;
  displayNumber: string;
  zoneId: string;
  status: Status;
  driverId: string | null;
  driverName: string | null;
  trailerId: string | null;
  trailerNumber: string | null;
  trailerType?: 'dry_van' | 'reefer' | 'flatbed' | 'tanker' | 'container';
  shipmentNumber: string | null;
  bol_id?: string | null;
  carrier_name?: string | null;
  scheduledTimes?: ScheduledTime[];
  current_status?: string | null;
  direction?: string | null;
  order_number?: number | null;
  trailerStatus?: string | null; // LOADED, EMPTY, etc.
  notes?: string; // Notes from configuration
  active_lineage?: {
    uuid: string;
    notes?: string;
    created_at?: string;
  };
  data?: any; // Add data field for trailer data including OOS status
};

export type HistoryEntry = {
  id: string;
  timestamp: Date;
  driverName: string;
  activity: 'loading' | 'unloading' | 'parked' | 'maintenance';
  duration: number;
  details?: string;
};

export type YardHistory = {
  [spotId: string]: HistoryEntry[];
};

// Custom hook to fetch yard data
export const useYardData = (facilityId?: number, refreshInterval: number = 30000) => {
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Core state for data
  const [yardSpots, setYardSpots] = useState<YardSpot[]>([]);
  const [yardHistory, setYardHistory] = useState<YardHistory>({});
  const [facilities, setFacilities] = useState<{ id: number; name: string }[]>([]);
  const [selectedFacility, setSelectedFacility] = useState<{ id: number; name: string } | null>(() => {
    // Initialize from localStorage on mount
    return loadFacilityFromStorage();
  });
  const [timeInterval, setTimeInterval] = useState('24');
  const [zones, setZones] = useState<Zone[]>([]);
  const [facilityZones, setFacilityZones] = useState<any[]>([]);
  
  // Add warehouse data state
  const [warehouseData, setWarehouseData] = useState<any>(null);

  // Lock-based state for tracking locked spots and trailers
  const [taskBasedLockedSpots, setTaskBasedLockedSpots] = useState<Set<string>>(new Set());
  const [taskBasedLockedTrailers, setTaskBasedLockedTrailers] = useState<Set<string>>(new Set());
  const [activeLocks, setActiveLocks] = useState<any[]>([]);
  const [allFacilityLocks, setAllFacilityLocks] = useState<any[]>([]);

  // Use refs for tracking fetching state without causing renders
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch facilities only once on mount (separate from consolidated query)
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        // GraphQL query for facilities only
        const facilitiesQuery = `
          query {
            facilities {
              id
              name
            }
          }
        `;

        // Get auth token from cookie or localStorage
        const authToken = getAuthToken();

        // Fetch facilities using GraphQL
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: facilitiesQuery
          })
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        setFacilities(result.data.facilities);
        
        // Validate localStorage facility still exists, otherwise set default
        const storedFacility = loadFacilityFromStorage();
        if (storedFacility) {
          const facilityExists = result.data.facilities.find((f: any) => f.id === storedFacility.id);
          if (!facilityExists) {
            // Stored facility no longer exists, clear it and set default
            if (facilityId) {
              const facility = result.data.facilities.find((f: any) => f.id === facilityId);
              if (facility) {
                setSelectedFacility(facility);
                saveFacilityToStorage(facility);
              }
            } else if (result.data.facilities.length > 0) {
              setSelectedFacility(result.data.facilities[0]);
              saveFacilityToStorage(result.data.facilities[0]);
            }
          }
          // If facility exists, keep the localStorage value (already set in useState initializer)
        } else {
          // No stored facility, set default
          if (facilityId) {
            const facility = result.data.facilities.find((f: any) => f.id === facilityId);
            if (facility) {
              setSelectedFacility(facility);
              saveFacilityToStorage(facility);
            }
          } else if (result.data.facilities.length > 0) {
            setSelectedFacility(result.data.facilities[0]);
            saveFacilityToStorage(result.data.facilities[0]);
          }
        }
      } catch (err) {
        setError('Failed to fetch facilities');
        console.error(err);
      }
    };
    
    fetchFacilities();
  }, [facilityId]);

  // Initialize base dock doors when facility changes
  useEffect(() => {
    if (!selectedFacility) {
      setYardSpots([]);
      setWarehouseData(null);
      return;
    }
    
    // Initialize with empty array, will be populated by consolidated GraphQL query
    setYardSpots([]);
    setWarehouseData(null);
  }, [selectedFacility]);

  // Consolidated data fetching function used for both initial load and refresh
  const fetchConsolidatedData = useCallback(async () => {
    if (!selectedFacility) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // Consolidated GraphQL query for yard, warehouse, facility, and locks data
      const consolidatedQuery = `
        query GetYardWarehouseAndFacilityData {
          yard(facility_id: "${selectedFacility ? selectedFacility.id : "1"}") {
            facility_id
            facility_zones {
              facility_zone_name
              facility_zone_uuid
              zone_type
              configuration
              trailers {
                uuid
                trailerNumber
                carrier{
                  name
                }
                active_lineage {
                  uuid
                  notes
                  created_at
                  trailer_state{
                    shipment {
                      id
                      referenceNumber
                      direction
                    }
                    driver {
                      id
                      firstName
                      lastName
                    }
                  }
                }
                status {
                  name
                }
                data
              }
              yard_spots {
                yard_spot_uuid
                yard_spot_name
                yard_spot_facility_zone_uuid
                yard_spot_status
                trailer_lineage_uuid
                trailer_state_uuid
                trailer_name
                trailer_uuid
                carrier_name
                shipment_id
                shipment_reference_number
                driver_id
                driver_name
                order_number
                configuration
                shipment {
                  id
                  referenceNumber
                  direction
                }
                driver {
                  id
                  firstName
                  lastName
                }
                trailer {
                  uuid
                  trailerNumber
                  carrier{
                    name
                  }
                  active_lineage {
                    uuid
                    notes
                    created_at
                  }
                  status {
                    name
                  }
                  data
                }
              }
            }
            zoneSummary {
              facilityZoneName
              facilityZoneUuid
              totalYardSpots
            }
            statusSummary {
              available_yard_spots
              empty_trailers
              loaded_inbound
              loaded_outbound
            }
          }
          warehouse(facilityId: "${selectedFacility ? selectedFacility.id : "1"}") {
            facilityId
            warehouseZones {
              zoneUuid
              zoneName
              configuration
              docks {
                dockUuid
                dockName
                dockStatus
                order_number
                trailer_name
                trailer_uuid
                carrier_name
                configuration
                trailer{
                  active_lineage {
                    uuid
                    notes
                    created_at
                  }
                }
                shipment {
                  id
                  referenceNumber
                  direction
                }
                driver {
                  id
                  firstName
                  lastName
                }
              }
            }
          }
          facility(id: "${selectedFacility ? selectedFacility.id : "1"}") {
            id
            configuration
          }
          facilityLocks(facilityId: "${selectedFacility ? selectedFacility.id : "1"}") {
            reason
            resource_uuid
            model_name
            operation_type
            created_by
            task_uuid
            created_at
            expires_at
            namespace
            facility_id
            resource {
                ... on YardSpotObject {
                  __typename
                  uuid
                  name
                }
                ... on DockObject {
                  __typename
                  uuid
                  name
                }
                ... on Trailer {
                  __typename
                  uuid
                  trailerNumber
                }
              }
            }
          }
        `;

      // Fetch consolidated data using GraphQL
      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: consolidatedQuery
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Debug: Log the consolidated GraphQL response
      console.log("Consolidated GraphQL Response (Yard + Warehouse + Facility + Locks):", result.data);
      
      // Process the warehouse data
      if (result.data && result.data.warehouse) {
        setWarehouseData(result.data.warehouse);
      } else {
        setWarehouseData(null);
      }
      
      // Process the yard data
      if (result.data && result.data.yard) {
        const yardData = result.data.yard;
        
        // Extract and process zones from the GraphQL response
        if (yardData.facility_zones && yardData.facility_zones.length > 0) {
          // Build the zones from GraphQL data
          const graphQLZones = yardData.facility_zones.map((zone: any) => ({
            id: zone.facility_zone_uuid,
            name: zone.facility_zone_name,
            color: zone.configuration?.color || 
                   (zone.zone_type === "staging" ? "#F59E0B" : "#2196F3"), // Use config color, or amber for staging, or blue as default
            zone_type: zone.zone_type
          }));

          // remove "Main Yard" from the zones
          const filteredZones = graphQLZones.filter((zone: any) => zone.name !== "Main Yard");
          
          setZones(filteredZones);
          
          // Process all yard spots from all zones
          const allSpots: YardSpot[] = [];
          
          yardData.facility_zones.forEach((zone: any) => {
            if (zone.yard_spots && zone.yard_spots.length > 0) {
              zone.yard_spots.forEach((spot: any) => {
                // Determine spot status - prioritize configuration status first
                let status: Status = 'available';
                
                // Treat configuration "unavailable" as an override; treat "available" as unset/unknown
                const configStatus = spot.configuration?.status;
                if (configStatus === 'unavailable') {
                  status = 'unavailable';
                } else if (spot.yard_spot_status === 'INACTIVE' || spot.yard_spot_status === 'inactive') {
                  status = 'inactive';
                } else if (spot.trailer && spot.trailer.status && spot.trailer.status.name) {
                  // Use trailer status to determine display
                  if (spot.trailer.status.name === 'LOADED') {
                    status = 'loaded'; // Blue tile
                  } else if (spot.trailer.status.name === 'EMPTY') {
                    status = 'empty'; // Yellow tile
                  } else {
                    status = 'in-use'; // Fallback for other trailer statuses
                  }
                } else if (spot.trailer_uuid) {
                  // Has trailer but no status - fallback to in-use
                  status = 'in-use';
                }
                
                // Determine direction - ONLY use shipment direction if available
                let direction: string | null = null;
                
                if (spot.shipment && spot.shipment.direction) {
                  direction = spot.shipment.direction;
                }
                
                // Map API spot to our internal YardSpot type
                allSpots.push({
                  id: spot.yard_spot_uuid,
                  number: parseInt(spot.yard_spot_name.replace(/\D/g, '')) || 0,
                  displayNumber: spot.yard_spot_name,
                  zoneId: zone.facility_zone_uuid,
                  status,
                  driverId: spot.driver_id || null,
                  driverName: spot.driver_name || null,
                  trailerId: spot.trailer_uuid || null,
                  trailerNumber: spot.trailer_name || null,
                  shipmentNumber: spot.shipment_reference_number || null,
                  bol_id: spot.shipment_id || null,
                  carrier_name: spot.carrier_name || null,
                  current_status: spot.yard_spot_status || null,
                  direction,
                  order_number: spot.order_number ? parseInt(spot.order_number, 10) : null,
                  trailerStatus: spot.trailer?.status?.name || null,
                  notes: spot.configuration?.notes || null, // Extract notes from configuration
                  active_lineage: spot.trailer?.active_lineage ? {
                    uuid: spot.trailer.active_lineage.uuid,
                    notes: spot.trailer.active_lineage.notes,
                    created_at: spot.trailer.active_lineage.created_at
                  } : undefined,
                  data: spot.trailer?.data || null, // Include trailer data for OOS access
                  scheduledTimes: [] // Initialize with empty scheduledTimes array
                });
              });
            }
          });
          
          // Sort spots within each zone by order_number
          const sortedSpots = allSpots.sort((a, b) => {
            // First sort by zone
            if (a.zoneId !== b.zoneId) {
              return a.zoneId.localeCompare(b.zoneId);
            }
            // Then sort by order_number within the same zone
            // Handle null/undefined values by treating them as very high numbers (put them at the end)
            const aOrderNumber = (a.order_number !== null && a.order_number !== undefined) ? a.order_number : Number.MAX_SAFE_INTEGER;
            const bOrderNumber = (b.order_number !== null && b.order_number !== undefined) ? b.order_number : Number.MAX_SAFE_INTEGER;
            return aOrderNumber - bOrderNumber;
          });
          
          setYardSpots(sortedSpots);
          
          // Store facility zones data for zone-based trailer access
          setFacilityZones(yardData.facility_zones);
        } else {
          // No yard data found
          setZones([]);
          setYardSpots([]);
          setFacilityZones([]);
        }
      } else {
        // No yard data found
        setZones([]);
        setYardSpots([]);
        setFacilityZones([]);
      }

      // Process facility locks to determine locked yard spots and trailers
      if (result.data && result.data.facilityLocks) {
        const locks = result.data.facilityLocks;
        const lockedSpotIds = new Set<string>();
        const lockedTrailerIds = new Set<string>();
        const activeLocks: any[] = [];
        
        // Store all facility locks for use by other components
        setAllFacilityLocks(locks);

        locks.forEach((lock: any) => {
          // Check if this lock applies to a yard spot
          const isYardSpotLock = lock.model_name === 'YardSpot' || 
                                lock.model_name === 'YardSpotObject' || 
                                (lock.resource && lock.resource.__typename === 'YardSpotObject');
          
          // Check if this lock applies to a trailer
          const isTrailerLock = lock.model_name === 'Trailer' || 
                               (lock.resource && lock.resource.__typename === 'Trailer');
          
          if (isYardSpotLock) {
            // Store the lock information for linking to tasks
            activeLocks.push(lock);
            
            // Add the yard spot UUID to locked spots
            const spotUuid = lock.resource_uuid || lock.resource?.uuid;
            if (spotUuid) {
              lockedSpotIds.add(spotUuid);
            }
          } else if (isTrailerLock) {
            // Store the lock information for linking to tasks
            activeLocks.push(lock);
            
            // Add the trailer UUID to locked trailers
            const trailerUuid = lock.resource_uuid || lock.resource?.uuid;
            if (trailerUuid) {
              lockedTrailerIds.add(trailerUuid);
            }
          }
        });

        setTaskBasedLockedSpots(lockedSpotIds);
        setTaskBasedLockedTrailers(lockedTrailerIds);
        setActiveLocks(activeLocks);
      } else {
        setTaskBasedLockedSpots(new Set());
        setTaskBasedLockedTrailers(new Set());
        setActiveLocks([]);
        setAllFacilityLocks([]);
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to fetch consolidated data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedFacility]);

  // Consolidated data fetching when facility changes
  useEffect(() => {
    if (!selectedFacility) return;
    fetchConsolidatedData();
  }, [selectedFacility, fetchConsolidatedData]);

  // Create a memoized refresh function that calls the consolidated fetch
  const refreshData = useCallback(async () => {
    await fetchConsolidatedData();
  }, [fetchConsolidatedData]);

  // Helper function to find locks affecting a specific yard spot
  const getLocksForYardSpot = (yardSpotId: string) => {
    const relevantLocks = activeLocks.filter((lock: any) => {
      const lockResourceUuid = lock.resource_uuid || lock.resource?.uuid;
      
      // Check if lock directly targets this yard spot
      if (lockResourceUuid === yardSpotId) {
        console.log(`Found direct yard spot lock for ${yardSpotId}:`, lock);
        return true;
      }
      
      return false;
    });
    
    console.log(`Total locks found for yard spot ${yardSpotId}:`, relevantLocks.length, relevantLocks);
    return relevantLocks;
  };

  // Wrapper function to save facility selection to localStorage
  const setSelectedFacilityWithStorage = (facility: { id: number; name: string } | null) => {
    setSelectedFacility(facility);
    saveFacilityToStorage(facility);
  };

  // Return the consolidated data and functionality
  return {
    loading,
    yardSpots,
    yardHistory,
    error,
    facilities,
    selectedFacility,
    setSelectedFacility: setSelectedFacilityWithStorage,
    timeInterval,
    setTimeInterval,
    zones,
    facilityZones,
    refreshData,
    // Warehouse data
    warehouseData,
    // Task-based locks
    taskBasedLockedSpots,
    taskBasedLockedTrailers,
    activeLocks,
    allFacilityLocks,
    getLocksForYardSpot
  };
}; 